/* eslint-disable max-lines */
import { axios } from "../config/axios";
import {
  IAddOnAnalyticsProps,
  IFilteredAnalyticsPerLineProps,
  IFilteredAnalyticsProps,
  IPeriodAnalyticsProps,
  ITodayAnalyticsProps,
  LineSalesPerDateModel,
  OfficeAndOnlineSalesAnalyticsModel,
  OperatorActiveLinesAndAgentsModel,
  OperatorAgentsSalesAnalyticsModel,
  OperatorBundleAnalyticsModel,
  OperatorFilteredAnalyticsListModel,
  TicketsToBePaidOnBusAnalyticsModel,
  TripSalesPerDateModel,
  TripsSalesAnalyticsModel
} from "../interfaces/endpoints/IAnalytics.types";
import { IAxiosResponse } from "../interfaces/IAxiosResponse";
import { serialize } from "../utils/serializeToQueryString";

// Gjirafa dashboard: computed live from source tables (every operator's fares plus Gjirafa's fees), same
// response shapes as the legacy dashboard/* endpoints, which read the Dashboard aggregate and are kept on the
// backend so this can be pointed back at them.
// "Sot" on its own call, so it never waits on the 7- and 30-day cards.
export const getTodayAnalytics = async (): Promise<
  IAxiosResponse<ITodayAnalyticsProps>
> => {
  return axios.get<ITodayAnalyticsProps>("v2/GjirafaDashboard/TodayAnalytics");
};

export const getPeriodAnalytics = async (): Promise<
  IAxiosResponse<IPeriodAnalyticsProps>
> => {
  return axios.get<IPeriodAnalyticsProps>("v2/GjirafaDashboard/PeriodAnalytics");
};

export const getFilteredAnalytics = async (params: {
  startDate: string;
  endDate: string;
}): Promise<IAxiosResponse<IFilteredAnalyticsProps>> => {
  return axios.get<IFilteredAnalyticsProps>(
    `v2/GjirafaDashboard/FilteredAnalytics${serialize(params)}`
  );
};

export const getAddOnAnalytics = async (params: {
  startDate: string;
  endDate: string;
}): Promise<IAxiosResponse<IAddOnAnalyticsProps>> => {
  return axios.get<IAddOnAnalyticsProps>(
    `v2/GjirafaDashboard/AddOnAnalytics${serialize(params)}`
  );
};

export const getFilteredAnalyticsPerLine = async (params: {
  startDate: string;
  endDate: string;
  lineId?: string;
  operatorId?: string;
}): Promise<IAxiosResponse<IFilteredAnalyticsPerLineProps>> => {
  return axios.get<IFilteredAnalyticsPerLineProps>(
    `v2/GjirafaDashboard/LineSearchAndReservation${serialize(params)}`
  );
};
// * New

export const getOperatorActiveLinesAndAgents = async (): Promise<
  IAxiosResponse<OperatorActiveLinesAndAgentsModel>
> => {
  return axios.get<OperatorActiveLinesAndAgentsModel>(
    "v1/OperatorDashboard/ActiveLinesAndAgents"
  );
};

export const getOperatorFilteredAnalytics = async (params: {
  startDate: string;
  endDate: string;
  lineId?: number;
}): Promise<IAxiosResponse<OperatorFilteredAnalyticsListModel>> => {
  return axios.get<OperatorFilteredAnalyticsListModel>(
    `v1/OperatorDashboard/FilteredAnalytics${serialize(params)}`
  );
};

export const getOperatorAgentSalesAnalytics = async (params: {
  startDate: string;
  endDate: string;
  lineId?: number;
}): Promise<IAxiosResponse<OperatorAgentsSalesAnalyticsModel>> => {
  return axios.get<OperatorAgentsSalesAnalyticsModel>(
    `v1/OperatorDashboard/AgentSalesAnalytics${serialize(params)}`
  );
};

export const getOperatorBundleAnalytics = async (params: {
  startDate: string;
  endDate: string;
}): Promise<IAxiosResponse<OperatorBundleAnalyticsModel>> => {
  return axios.get<OperatorBundleAnalyticsModel>(
    `v1/OperatorDashboard/BundleAnalytics${serialize(params)}`
  );
};

export const getOperatorTicketsToBePaidOnBusAnalytics = async (params: {
  startDate: string;
  endDate: string;
  lineId?: number;
}): Promise<IAxiosResponse<TicketsToBePaidOnBusAnalyticsModel>> => {
  return axios.get<TicketsToBePaidOnBusAnalyticsModel>(
    `v1/OperatorDashboard/TicketsToBePaidOnBusAnalytics${serialize(params)}`
  );
};

export const getOperatorOfficeAndOnlineSalesAnalytics = async (params: {
  startDate: string;
  endDate: string;
  lineId?: number;
}): Promise<IAxiosResponse<OfficeAndOnlineSalesAnalyticsModel>> => {
  return axios.get<OfficeAndOnlineSalesAnalyticsModel>(
    `v1/OperatorDashboard/OfficeAndOnlineSalesAnalytics${serialize(params)}`
  );
};

export const getOperatorTripsSalesAnalytics = async (params: {
  startDate: string;
  endDate: string;
  lineId?: number;
}): Promise<IAxiosResponse<TripsSalesAnalyticsModel[]>> => {
  return axios.get<TripsSalesAnalyticsModel[]>(
    `v1/OperatorDashboard/TripsSalesAnalytics${serialize(params)}`
  );
};

// v2 operator dashboard: same response shape as v1, but computed live from source tables (no Dashboard
// aggregate). The v1 functions above are intentionally kept so a cutover can be reverted by swapping back.
export const getOperatorFilteredAnalyticsV2 = async (params: {
  startDate: string;
  endDate: string;
  lineId?: number;
}): Promise<IAxiosResponse<OperatorFilteredAnalyticsListModel>> => {
  return axios.get<OperatorFilteredAnalyticsListModel>(
    `v2/OperatorDashboard/FilteredAnalytics${serialize(params)}`
  );
};

export const getOperatorTripsSalesAnalyticsV2 = async (params: {
  startDate: string;
  endDate: string;
  lineId?: number;
}): Promise<IAxiosResponse<TripsSalesAnalyticsModel[]>> => {
  return axios.get<TripsSalesAnalyticsModel[]>(
    `v2/OperatorDashboard/TripsSalesAnalytics${serialize(params)}`
  );
};

export const getOperatorTripSalesPerDate = async (params: {
  dateFrom: string;
  dateTo: string;
  tripId?: string;
}): Promise<IAxiosResponse<TripSalesPerDateModel>> => {
  return axios.get<TripSalesPerDateModel>(
    `v1/OperatorDashboard/TripSalesPerDate${serialize(params)}`
  );
};
export const getOperatorSalesPerDate = async (params: {
  dateFrom: string;
  dateTo: string;
  lineId?: string;
}): Promise<IAxiosResponse<LineSalesPerDateModel[]>> => {
  return axios.get<LineSalesPerDateModel[]>(
    `v1/OperatorDashboard/SalesPerDate${serialize(params)}`
  );
};
